<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import EvolutionConnectionApi from 'dashboard/api/inbox/evolutionConnection';
import Banner from 'dashboard/components/ui/Banner.vue';
import EvolutionConnectModal from './EvolutionConnectModal.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const CHECK_INTERVAL_MS = 20_000;

const disconnectedInbox = ref(null);
const alertReason = ref('disconnected'); // 'disconnected' | 'unreachable'
const dismissed = ref(false);
const showModal = ref(false);
let timer = null;

const apiInboxes = computed(() =>
  (getters.getInboxes.value || []).filter(
    inbox => inbox.channel_type === 'Channel::Api'
  )
);

const shouldShowBanner = computed(
  () => isAdmin.value && !dismissed.value && !!disconnectedInbox.value
);

const bannerMessage = computed(() => {
  const key =
    alertReason.value === 'unreachable'
      ? 'INBOX_MGMT.EVOLUTION.BANNER.MESSAGE_UNREACHABLE'
      : 'INBOX_MGMT.EVOLUTION.BANNER.MESSAGE';
  return t(key, { inboxName: disconnectedInbox.value?.name || '' });
});

const checkConnections = async () => {
  // Nao assume que outro componente ja carregou a lista de inboxes: o
  // banner pode ser o primeiro a montar (raiz do App.vue).
  if (!getters.getInboxes.value?.length) {
    await store.dispatch('inboxes/get');
  }
  for (const inbox of apiInboxes.value) {
    try {
      const { data } = await EvolutionConnectionApi.status(inbox.id);
      if (!data.connected) {
        disconnectedInbox.value = inbox;
        alertReason.value = 'disconnected';
        return;
      }
    } catch (error) {
      // 422 = este inbox nao tem Evolution configurada: nao e um problema,
      // apenas um inbox que nao usa esse recurso.
      if (error?.response?.status === 422) continue;
      // Qualquer outro erro (API fora do ar, timeout, 5xx) e um sinal real
      // de que nao sabemos o estado da conexao - para um requisito de
      // "extrema importancia" o correto e alertar, nao ficar em silencio.
      disconnectedInbox.value = inbox;
      alertReason.value = 'unreachable';
      return;
    }
  }
  disconnectedInbox.value = null;
};

const onConnected = () => {
  showModal.value = false;
  disconnectedInbox.value = null;
};

onMounted(() => {
  checkConnections();
  timer = setInterval(checkConnections, CHECK_INTERVAL_MS);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <Banner
    v-if="shouldShowBanner"
    color-scheme="alert"
    :banner-message="bannerMessage"
    has-action-button
    :action-button-label="t('INBOX_MGMT.EVOLUTION.BANNER.ACTION')"
    action-button-icon="i-lucide-qr-code"
    has-close-button
    @primary-action="showModal = true"
    @close="dismissed = true"
  />
  <EvolutionConnectModal
    v-if="showModal"
    :inbox="disconnectedInbox"
    @close="showModal = false"
    @connected="onConnected"
  />
</template>
